import axios from 'axios';
import { FieldType } from '../../../typings/bindings/forms/FieldType';
import { Field } from '../../atoms/forms';
import { FilterId } from '../../components_v2/filter/model/Model';
import { FilterParameter } from '../../components_v2/filter/pages/FilterList';
import { URL_FOUNDATION } from '../../config/keys';
import { translateToString } from '../../styles/global/translate';
import { DefaultTableQuery } from '../checkouts/actions';
import { CompanyDetail, ServerAdvancedFilter } from '../client-companies/model/Model';
import { Screen } from './jsonValidator';

export type FormInstance = {
	count: number,
	started_at: string,
	made_at: string,
	created_at: string,
	progression: number,
	created_by: number,
	uuid: string,
	linked_company: string,
	name: string,
	additional_field_columns_value: { [key: string]: { value: unknown } },
	updated_at?: string
}

export async function getFormInstances(id: number, body?: DefaultTableQuery): Promise<FormInstance[]> {
	return axios.post(`${URL_FOUNDATION}/api/v2/forms/instances/${id}`, body ?? {}).then(res => res.data);
}


type FormInstanceGalleryImage = {
	value: { name: string, url: string, url_thumb: string }[];
	metadata: object;
	field_name: string;
};

export type FormInstanceGalleryType = {
	count: number;
	uuid: string;
	images: FormInstanceGalleryImage[];
	progression: number;
	created_by: number;
	made_at?: string;
	linked_company_id?: number;
	linked_company_name?: string;
	linked_event?: string;
};

export async function getFormInstancesGallery(id: number, body: DefaultTableQuery): Promise<FormInstanceGalleryType[]> {
	return axios.post(`${URL_FOUNDATION}/api/v2/forms/instances/${id}/gallery`, body).then(res => res.data);
}

export async function getFormInstancesFilters(): Promise<{ filters: FilterParameter[], columns: FilterId[] }> {
	return axios.get<ServerAdvancedFilter[]>(`${URL_FOUNDATION}/api/v2/forms/instances/filters`).then(res => {
		return {
			filters: [res.data.reduce((acc: FilterParameter, f) => {
				acc.filters.push({
					id: f[0],
					name: typeof (f[0]) === 'string' ? translateToString(f[0]) : 'no translation',
					type: f[1]
				});
				return acc;
			}, { category: translateToString('forms'), filters: [] })],
			columns: res.data.map(c => c[0]),
		};
	});
}

export type KanbanFormInstance = {
	uuid: string,
	made_at: string,
	progression: number,
	created_by: number,
	linked_company?: string,
	value: string | null,
	field_value_uuid: string | null,
}

export async function getKanbanFormInstances(id: number, fieldId: number, body: DefaultTableQuery): Promise<KanbanFormInstance[]> {
	return axios.post(`${URL_FOUNDATION}/api/v2/forms/instances/${id}/kanban/${fieldId}`, body).then(res => res.data);
}

export type FormInstanceAndFormId = FormInstance & { form_id: number }

export async function getLinkedFormInstance(id: number): Promise<{ [key: string]: FormInstanceAndFormId[] }> {
	const res = await axios.get<FormInstanceAndFormId[]>(`${URL_FOUNDATION}/api/v2/forms/instances/linked/${id}`);
	const sres = res.data.reduce<{ [key: string]: FormInstanceAndFormId[] }>((acc, f) => {
		if (!acc[f.name]) {
			acc[f.name] = [];
		}
		acc[f.name].push(f);
		return acc;
	}, {});
	return sres;
}

export function deleteFormInstance(id: string): Promise<void> {
	return axios.delete(`${URL_FOUNDATION}/api/v2/forms/instances/${id}`);
}

export type TypedMetadata = {
	key: string,
	type: 'campaign' | 'product' | 'catalogue' | 'string' | 'bool' | 'numeric',
	created_at: string,
	updated_at: string,
	updated_by: number,
}

export type MetadataType = TypedMetadata['type'];

export async function getTypedMetadata(): Promise<TypedMetadata[]> {
	return axios.get(`${URL_FOUNDATION}/api/v2/forms/typed-metadata`).then(res => res.data);
}

export async function postTypedMetadata(body: { key: string, type: MetadataType }): Promise<void> {
	return axios.post(`${URL_FOUNDATION}/api/v2/forms/typed-metadata`, body);
}

export async function putTypedMetadata(body: { key: string, type: MetadataType }): Promise<void> {
	return axios.put(`${URL_FOUNDATION}/api/v2/forms/typed-metadata`, body);
}

export async function deleteTypedMetadata(key: string): Promise<void> {
	return axios.delete(`${URL_FOUNDATION}/api/v2/forms/typed-metadata?key=${key}`);
}

export async function generateMetadata(): Promise<void> {
	return axios.post(`${URL_FOUNDATION}/api/v2/forms/typed-metadata/generate`);
}

export type Metadata = {
	[key: string]: string
}

export type InstanceField = {
	id: number,
	uuid: string,
	type: FieldType,
	value: object | string | number | Date,
	metadata: Metadata,
	name: string,
	slug: string,
}


export function getInstanceFields(id: string): Promise<InstanceField[]> {
	return axios.post(`${URL_FOUNDATION}/api/v2/forms/instance-fields/${id}`).then(res => res.data);
}

export type Instance = {
	uuid: string,
	form_id: number,
	made_at?: string,
	created_by: number,
	progression: number,
	started_at?: string,
	linked_event?: number,
	name: string,
	screens: Screen[],
	linked_company_id?: number,
	linked_company_name?: string,
	created_at: string,
}

export async function getInstanceByUuid(uuid: string): Promise<Instance> {
	return axios.get(`${URL_FOUNDATION}/api/v2/forms/instance/${uuid}`).then(res => res.data);
}

export async function getField(id: number): Promise<Field> {
	return axios.get(`${URL_FOUNDATION}/api/v2/forms/field/${id}`).then(res => res.data);
}

export async function putField(id: number, body: Omit<Field, 'slug'>): Promise<void> {
	return axios.put(`${URL_FOUNDATION}/api/v2/forms/field/${id}`, body);
}

export function postForm(body): Promise<void> {
	return axios.post(`${URL_FOUNDATION}/api/v2/forms/templates`, body);
}

export function postField(body): Promise<Field> {
	return axios.post(`${URL_FOUNDATION}/api/v2/forms/fields`, body).then(res => res.data);
}

export function deleteFormTemplate(id: number): Promise<void> {
	return axios.delete(`${URL_FOUNDATION}/api/v2/forms/templates/${id}`);
}

export function deleteField(id: number): Promise<void> {
	return axios.delete(`${URL_FOUNDATION}/api/v2/forms/field/${id}`);
}

export async function getClientCompanyByUuid(companyUuid: string): Promise<CompanyDetail> {
	return axios.get(`${URL_FOUNDATION}/api/v2/client-companies/uuid/${companyUuid}`).then(res => res.data);
}

export async function exportFieldValues(formId: number, formName: string, body: DefaultTableQuery): Promise<void> {
	const blob = await axios.post(`${URL_FOUNDATION}/api/v2/export/field-values?form_id=${formId}`, body, { responseType: 'blob' });
	const href = window.URL.createObjectURL(blob.data);
	const link = document.createElement('a');
	link.href = href;
	link.setAttribute('download', `export-${formName}-${(new Date()).toISOString()}.csv`);
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(href);
}

export async function exportGroupedFieldValues(formId: number, formName: string, body: DefaultTableQuery): Promise<void> {
	const blob = await axios.post(`${URL_FOUNDATION}/api/v2/export/grouped?form_id=${formId}`, body, { responseType: 'blob' });
	const href = window.URL.createObjectURL(blob.data);
	const link = document.createElement('a');
	link.href = href;
	link.setAttribute('download', `export-${formName}-${(new Date()).toISOString()}.csv`);
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(href);
}

/// Corrects the completion percentage of an instance by hand; the server
/// bumps the instance's updated_at like any other edit.
export async function putInstanceProgression(uuid: string, progression: number): Promise<void> {
	return axios.put(`${URL_FOUNDATION}/api/v2/forms/instance/${uuid}/progression`, { progression }).then(() => undefined);
}

export async function putFieldValue(body: { uuid?: string, field_id: number, form_instance_id: string, value: unknown, metadata: unknown }): Promise<string | undefined> {
	return axios.put(`${URL_FOUNDATION}/api/v2/forms/field_values`, body).then(res => res.data);
}

export async function postFieldValue(body: { field_id: number, form_instance_id: string, value: unknown, metadata: unknown }): Promise<string | undefined> {
	return axios.post(`${URL_FOUNDATION}/api/v2/forms/field_values`, body).then(res => res.data);
}

type MetadataPeriod = {
	metadata: string
}

export async function getMetadataPeriods(): Promise<MetadataPeriod[]> {
	return axios.get(`${URL_FOUNDATION}/api/v2/forms/field_values/metadata_periods`).then(res => res.data);
}

export type View = {
	id: number
	name: string
	template: string
	sequence: number
	created_at?: string
	updated_at?: string
	linked_form_ids: Array<number>
}

type PutView = {
	id: number
	name?: string
	template?: string
	sequence?: number
}

type PutViewRelations = {
	id: number
	added_linked_forms?: Array<number>
	removed_linked_forms?: Array<number>
}

type PostView = Omit<View, 'id' | 'linked_form_ids' | 'created_at' | 'updated_at'>;

export type NewView = Omit<View, 'id' | 'sequence'> & Partial<Pick<View, 'id' | 'sequence'>>;

export async function getAllFormViews(): Promise<View[]> {
	return axios.get(`${URL_FOUNDATION}/api/v2/forms/views`).then(res => res.data.sort((a, b) => a.sequence - b.sequence));
}

export async function deleteFormView(formId: number): Promise<undefined> {
	return axios.delete(`${URL_FOUNDATION}/api/v2/forms/views?form_id=${formId}`);
}

export async function postFormView(body: PostView): Promise<string | undefined> {
	return axios.post(`${URL_FOUNDATION}/api/v2/forms/views`, body).then(res => res.data);
}

export async function putFormView(body: PutView): Promise<string | undefined> {
	return axios.put(`${URL_FOUNDATION}/api/v2/forms/views`, body).then(res => res.data);
}

export async function putFormViewRelations(body: PutViewRelations): Promise<string | undefined> {
	return axios.put(`${URL_FOUNDATION}/api/v2/forms/views/relations`, body).then(res => res.data);
}

export async function getFormViews(formId: number): Promise<View[]> {
	return axios.get(`${URL_FOUNDATION}/api/v2/forms/views?form_id=${formId}`).then(res => res.data.sort((a, b) => a.sequence - b.sequence));
}

export async function getFormViewById(viewId: number): Promise<View> {
	return axios.get(`${URL_FOUNDATION}/api/v2/forms/views/${viewId}`).then(res => res.data);
}
